import { type MetaFunction } from '@remix-run/node'

export const meta: MetaFunction = () => [
	{ title: 'Privacy Policy | Oui Technologies' },
	{
		name: 'description',
		content:
			'Privacy Policy for Oui Technologies — how we collect, use, store, and protect your information.',
	},
]

export default function PrivacyRoute() {
	return (
		<div className="bg-white py-16 px-6">
			<div className="max-w-3xl mx-auto">
				<h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
				<p className="text-sm text-gray-500 mb-10">Last Updated: June 12, 2026</p>

				<Section number="1" title="Who We Are">
					<p>
						Oui Technologies ("Oui," "we," "us") is registered in the State of
						Wisconsin, USA. This policy describes how we collect, use, store, and
						protect information through the Oui Technologies platform (the
						"Platform").
					</p>
				</Section>

				<Section number="2" title="Information We Collect">
					<div className="space-y-6">
						<div>
							<h3 className="font-semibold mb-2">
								2.1 Account &amp; Business Data
							</h3>
							<ul className="list-disc list-inside space-y-1 text-gray-700">
								<li>
									Restaurant/business account information (name, address, contact
									details)
								</li>
								<li>
									Invoice images and extracted invoice data (vendor names, line
									items, prices, quantities, dates)
								</li>
								<li>
									Inventory and recipe data derived from invoice information
								</li>
							</ul>
						</div>

						<div>
							<h3 className="font-semibold mb-2">
								2.2 Employee/Staff Data (Scheduling Feature)
							</h3>
							<p className="mb-2">
								If you use the scheduling function, we collect personal
								information about your employees, including:
							</p>
							<ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
								<li>Full name</li>
								<li>Phone number</li>
								<li>Email address</li>
								<li>Home address</li>
							</ul>
							<p>
								This information is provided by you (the business account holder)
								on behalf of your employees.{' '}
								<span className="font-semibold">
									You are responsible for ensuring you have the legal right and
									any required consents to provide this information to us.
								</span>
							</p>
						</div>

						<div>
							<h3 className="font-semibold mb-2">2.3 Information You Enter</h3>
							<p>
								Any other information voluntarily entered into the Platform by
								you or your authorized users.
							</p>
						</div>
					</div>
				</Section>

				<Section number="3" title="How We Use Information">
					<p className="mb-3">
						We use collected information solely to:
					</p>
					<ul className="list-disc list-inside space-y-1 text-gray-700 mb-4">
						<li>
							Operate and provide the Platform's core functions (invoice
							digitization, inventory tracking, recipe costing, employee
							scheduling)
						</li>
						<li>
							Sync invoice data to your connected QuickBooks account
						</li>
						<li>Provide customer support</li>
						<li>Maintain and improve Platform functionality</li>
					</ul>
					<p>
						We do{' '}
						<span className="font-semibold">not</span> sell user data, and we do
						not share user data with third parties for marketing or advertising
						purposes.
					</p>
				</Section>

				<Section number="4" title="Third Parties Who Process Your Data">
					<p className="mb-4">
						We share data with the following service providers ("sub-processors")
						solely to operate the Platform:
					</p>
					<div className="overflow-x-auto">
						<table className="w-full text-sm text-left border border-gray-200 rounded-lg overflow-hidden">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">
										Provider
									</th>
									<th className="px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">
										Purpose
									</th>
									<th className="px-4 py-3 font-semibold text-gray-700 border-b border-gray-200">
										Data Involved
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200 text-gray-700">
								<tr>
									<td className="px-4 py-3 font-medium">Fly.io</td>
									<td className="px-4 py-3">
										Application hosting and data storage
									</td>
									<td className="px-4 py-3">All Platform data</td>
								</tr>
								<tr className="bg-gray-50">
									<td className="px-4 py-3 font-medium">Intuit QuickBooks</td>
									<td className="px-4 py-3">
										Invoice data sync for accounting
									</td>
									<td className="px-4 py-3">
										Invoice data you choose to sync
									</td>
								</tr>
								<tr>
									<td className="px-4 py-3 font-medium">Namecheap</td>
									<td className="px-4 py-3">Domain registration</td>
									<td className="px-4 py-3">None (no user data)</td>
								</tr>
							</tbody>
						</table>
					</div>
					<p className="mt-3 text-sm text-gray-600">
						We may update this list as our service providers change. Material
						changes will be communicated per Section 9.
					</p>
				</Section>

				<Section number="5" title="Who Can Access Your Data">
					<p>
						Authorized Oui Technologies personnel — based in Madison, Wisconsin
						(USA) and London (UK) — may access account and Platform data solely
						to provide technical support, troubleshooting, and maintenance.
						Access is limited to what is necessary to resolve the issue at hand.
					</p>
				</Section>

				<Section number="6" title="International Data Transfers">
					<p>
						Because our support team includes personnel in the United Kingdom,
						data may be accessed from outside the country in which it was
						originally entered.
					</p>
				</Section>

				<Section number="7" title="Data Retention">
					<p>
						We retain account, invoice, inventory, and employee scheduling data
						for as long as your account is active, and for a period after account
						closure, after which it is deleted, except where retention is
						required by law or for legitimate business records (e.g., billing
						disputes).
					</p>
				</Section>

				<Section number="8" title="Your Rights">
					<p className="mb-3">
						Depending on your location, you may have rights to access, correct,
						delete, or export your data. To exercise these rights, contact us at{' '}
						<a
							href="mailto:support@ouiwiwe.com"
							className="text-primary underline"
						>
							support@ouiwiwe.com
						</a>
						.
					</p>
					<p>
						If you are a business account holder who has entered employee data,
						you are responsible for fulfilling any data subject requests from
						your employees; we will assist you in doing so.
					</p>
				</Section>

				<Section number="9" title="Changes to This Policy">
					<p>
						We may update this policy from time to time. Material changes will
						be communicated via email or in-app notice before they take effect.
					</p>
				</Section>

				<Section number="10" title="Security">
					<p>
						We use commercially reasonable technical and organizational measures
						to protect data, including encryption in transit and at rest, and
						access controls limiting who can view customer information.
					</p>
				</Section>

				<Section number="11" title="Governing Law">
					<p>
						This policy is governed by the laws of the State of Wisconsin, USA,
						without regard to conflict-of-law principles.
					</p>
				</Section>

				<Section number="12" title="Contact">
					<address className="not-italic text-gray-700 space-y-1">
						<p className="font-semibold">Oui Technologies</p>
						<p>1818 Thackeray Rd.</p>
						<p>Madison, WI 53704</p>
						<p>
							<a
								href="mailto:support@ouiwiwe.com"
								className="text-primary underline"
							>
								support@ouiwiwe.com
							</a>
						</p>
					</address>
				</Section>
			</div>
		</div>
	)
}

function Section({
	number,
	title,
	children,
}: {
	number: string
	title: string
	children: React.ReactNode
}) {
	return (
		<section className="mb-10">
			<h2 className="text-xl font-bold mb-3 text-gray-900">
				{number}. {title}
			</h2>
			<div className="text-gray-700 leading-relaxed">{children}</div>
		</section>
	)
}
